import React from 'react'
import { useFormikContext } from 'formik'

import ProfileFormWrapper from 'components/wrappers/ProfileFormWrapper'

import { VideosSlider } from './helpers'
import useAttributes from 'utils/hooks/useAttributes'

import { ProfileFormValues } from '../config'
import { CatalogItem } from 'shared'
import CheckList from 'components/CheckList'
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Radio,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme) => {
  return {
    sliderContainer: {
      marginInline: '-50px',
      [theme.breakpoints.down('sm')]: {
        marginInline: '-4px',
      },
    },
  }
})

const ProfileFormReviewExperience = () => {
  const classes = useStyles()
  const { values, errors, setValues } = useFormikContext<ProfileFormValues>()

  const { attributes, isLoading } = useAttributes([
    'level_of_interest',
    'content_types',
  ])

  const { level_of_interest, content_types } = attributes

  const onChangeContentTypes = (selectedItem: CatalogItem) => {
    const options = values.getValuesByAttribute(
      'content_types',
      'CONTENT',
      values.attributes
    )

    const catalogItems: CatalogItem[] = options.map((option) => ({
      code: String(option.value),
      text: option.label,
    }))

    if (
      catalogItems?.length > 0 &&
      catalogItems.find((item) => item.code === selectedItem.code)
    ) {
      setValues(
        values.setAttributeValue({
          attribute: 'content_types',
          category: 'CONTENT',
          value: catalogItems.filter((item) => item.code !== selectedItem.code),
        })
      )
    } else {
      const currentValues = [...catalogItems]
      currentValues.push(selectedItem)
      setValues(
        values.setAttributeValue({
          attribute: 'content_types',
          category: 'CONTENT',
          value: currentValues,
        })
      )
    }
  }

  return (
    <ProfileFormWrapper
      title="Video review experience"
      stepDescription="Please let us know your comfort and experience level with creating short form video reviews. You can explore a few example videos below to become familiar with the Hue review format!"
    >
      <Grid className={classes.sliderContainer} mb={5.5}>
        <VideosSlider />
      </Grid>
      <Grid mb={2.5}>
        <CheckList
          title="What is your level of experience in creating short form video reviews?"
          error={errors.validationErrors?.level_of_interest}
          isLoading={isLoading}
        >
          <Grid container flexDirection="column" gap={0.5}>
            {level_of_interest?.options?.map((item) => {
              const { code, text } = item
              return (
                <Grid key={code}>
                  <FormControlLabel
                    control={
                      <Radio
                        color="mint"
                        size="small"
                        disableRipple
                        checked={
                          code ===
                          values.getValueByAttribute(
                            'level_of_interest',
                            'CONTENT',
                            values.attributes
                          )?.value
                        }
                        onChange={() => {
                          setValues(
                            values.setAttributeValue({
                              attribute: 'level_of_interest',
                              category: 'CONTENT',
                              value: item,
                            })
                          )
                        }}
                      />
                    }
                    label={<Typography fontSize="14px">{text}</Typography>}
                  />
                </Grid>
              )
            })}
          </Grid>
        </CheckList>
      </Grid>
      <Grid>
        <CheckList
          title="Which product categories are you interested in reviewing? Select all that apply. Note: You won’t be restricted to the categories you choose - you’ll still be eligible to apply for all active campaigns if you’re selected to join the community!"
          error={errors.validationErrors?.content_types}
          isLoading={isLoading}
        >
          <Grid container flexDirection="column" gap={0.5}>
            {content_types?.options?.map((item) => {
              const { code, text } = item
              return (
                <Grid key={code}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="mint"
                        size="small"
                        disableRipple
                        checked={
                          !!values
                            .getValuesByAttribute(
                              'content_types',
                              'CONTENT',
                              values.attributes
                            )
                            ?.find((el) => el.value === code)
                        }
                        onChange={() => onChangeContentTypes(item)}
                      />
                    }
                    label={<Typography fontSize="14px">{text}</Typography>}
                  />
                </Grid>
              )
            })}
          </Grid>
        </CheckList>
      </Grid>
    </ProfileFormWrapper>
  )
}

export default ProfileFormReviewExperience
